// ** Modules
import React from 'react';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { useSession } from 'next-auth/react';
import axios from 'axios';

const firebaseConfig = {
	apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
	authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
	projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
	storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
	appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID
};

const FirebaseInitializer: React.FC = () => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_isSupported, setIsSupported] = React.useState(false);
	const { data: session, status } = useSession();

	React.useEffect(() => {
		const saveFcmTokenToStrapi = async (token: string) => {
			if (!session?.user) {
				console.warn('User not authenticated, skipping FCM token save');
				return;
			}
			try {
				await axios.post('/api/fcm-token', {
					token,
					userId: session.user.id
				});
				// const response = await axios.post('/api/fcm-token', {
				// 	token,
				// 	userId: session.user.id
				// });
				// console.log('FCM token saved to Strapi:', response.data);
				localStorage.setItem('fcmTokenSaved', 'true');
			} catch (error) {
				console.error('Error saving FCM token to Strapi:', error);
			}
		};

		const shouldRefreshToken = (): boolean => {
			const lastRefresh = localStorage.getItem('fcmTokenLastRefresh');
			if (!lastRefresh) return true;
			const refreshInterval = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
			return Date.now() - parseInt(lastRefresh, 10) > refreshInterval;
		};

		const initializeFirebase = async () => {
			if (!(await isSupported())) {
				console.warn('Firebase messaging is not supported in this browser');
				setIsSupported(false);
				return;
			}

			if (typeof window !== 'undefined' && 'serviceWorker' in navigator && 'Notification' in window) {
				try {
					const app = initializeApp(firebaseConfig);
					const messaging = getMessaging(app);

					const registration = await navigator.serviceWorker.register('/api/firebase-messaging-sw');
					// console.log('Service Worker registered:', registration.scope);

					const permissionGranted = localStorage.getItem('fcmPermissionGranted');

					if (!permissionGranted || shouldRefreshToken()) {
						const permission = await Notification.requestPermission();
						if (permission !== 'granted') {
							console.log('Notification permission not granted');
							return;
						}
						localStorage.setItem('fcmPermissionGranted', 'true');

						const token = await getToken(messaging, {
							vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
							serviceWorkerRegistration: registration
						});
						// console.log('FCM Token:', token);

						if (session?.user) {
							await saveFcmTokenToStrapi(token);
						}

						localStorage.setItem('fcmTokenLastRefresh', Date.now().toString());
					}

					onMessage(messaging, (payload) => {
						console.log('Received foreground message:', payload);
						const clickAction = payload.data?.clickAction;

						if (payload.notification) {
							new Notification(payload.notification.title || 'Notification', {
								body: payload.notification.body || 'You have a new message'
							}).onclick = () => {
								// Navigate to the url when the notification is clicked
								window.open(clickAction, '_blank');
							};
						} else {
							console.error('Notification payload is undefined');
						}
					});

					setIsSupported(true);
				} catch (error) {
					console.error('Error initializing Firebase:', error);
					setIsSupported(false);
				}
			} else {
				console.warn('Firebase messaging not supported in this environment');
				setIsSupported(false);
			}
		};

		if (status === 'authenticated') {
			initializeFirebase();
		}
	}, [status, session]);

	return null;
};

export default FirebaseInitializer;
